import { fileApi } from '@/api/endpoints';
import type { MediaInput, MediaType } from '@/api/types';

/** 썸네일 규격: 긴 변 400px, JPG 품질 0.85 */
const THUMBNAIL_MAX_EDGE = 400;
const THUMBNAIL_QUALITY = 0.85;
/** 재생할 수 없는 코덱이면 이벤트가 오지 않을 수 있어 무한정 기다리지 않는다. */
const VIDEO_LOAD_TIMEOUT_MS = 10_000;

const VIDEO_EXTENSIONS = ['mp4', 'm4v', 'webm', 'mov'];

export const MEDIA_ACCEPT = 'image/*,video/mp4,video/webm,video/quicktime,video/x-m4v,.m4v,.mov';

/**
 * 폼이 들고 있는 첨부 하나.
 * file 이 있으면 아직 S3 에 올리지 않은 것이고, 저장 버튼을 누를 때 uploadDrafts 로 올린다.
 */
export interface DraftMedia {
  /** React key 겸 식별자 */
  id: string;
  mediaType: MediaType;
  /** 미리보기 주소. 새로 고른 파일이면 blob: URL */
  previewUrl: string;
  /** 썸네일 미리보기 주소. 만들지 못했으면 비어 있다. */
  posterUrl?: string;
  file?: File;
  thumbnail?: Blob | null;
  url?: string;
  objectKey?: string;
  thumbnailUrl?: string;
  thumbnailObjectKey?: string;
}

let seq = 0;
const nextId = () => `media-${Date.now()}-${seq++}`;

export function detectMediaType(file: File): MediaType | null {
  if (file.type.startsWith('image/')) return 'IMAGE';
  if (file.type.startsWith('video/')) return 'VIDEO';
  // m4v·mov 는 브라우저/OS 에 따라 type 이 비어 오기도 한다.
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return VIDEO_EXTENSIONS.includes(extension) ? 'VIDEO' : null;
}

/** 새로 고른 파일로 미리보기와 썸네일을 만든다. 네트워크 요청은 없다. */
export async function createDraft(file: File, mediaType: MediaType): Promise<DraftMedia> {
  const thumbnail = await makeThumbnail(file, mediaType).catch(() => null);
  return {
    id: nextId(),
    mediaType,
    previewUrl: URL.createObjectURL(file),
    posterUrl: thumbnail ? URL.createObjectURL(thumbnail) : undefined,
    file,
    thumbnail,
  };
}

/** 이미 저장된 미디어(수정 화면)를 폼 상태로 옮긴다. */
export function draftFromSaved(media: {
  mediaType: MediaType;
  url: string;
  objectKey?: string;
  thumbnailUrl?: string;
  thumbnailObjectKey?: string;
}): DraftMedia {
  return {
    id: nextId(),
    mediaType: media.mediaType,
    previewUrl: media.url,
    posterUrl: media.thumbnailUrl,
    url: media.url,
    objectKey: media.objectKey,
    thumbnailUrl: media.thumbnailUrl,
    thumbnailObjectKey: media.thumbnailObjectKey,
  };
}

/** createDraft 가 만든 blob: URL 을 놓아준다. */
export function releaseDraft(draft: DraftMedia) {
  for (const url of [draft.previewUrl, draft.posterUrl]) {
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
  }
}

/**
 * 아직 올리지 않은 첨부를 차례로 올리고, 모두 url 이 채워진 목록을 돌려준다.
 * 미리보기 주소와 id 는 그대로 두므로 폼 상태를 이 결과로 바꿔도 화면이 깜빡이지 않고,
 * 저장 API 가 실패해 다시 눌러도 같은 파일을 두 번 올리지 않는다.
 *
 * @param onProgress 전체 바이트 기준 0~100
 */
export async function uploadDrafts(
  drafts: DraftMedia[],
  prefix: string,
  onProgress?: (percent: number) => void,
): Promise<DraftMedia[]> {
  const pending = drafts.filter((draft) => draft.file);
  const total = pending.reduce((sum, draft) => sum + draft.file!.size + (draft.thumbnail?.size ?? 0), 0);
  let done = 0;

  const result: DraftMedia[] = [];
  for (const draft of drafts) {
    if (!draft.file) {
      result.push(draft);
      continue;
    }
    const uploaded = await fileApi.upload(draft.file, prefix, {
      thumbnail: draft.thumbnail,
      onProgress: (loaded) => total > 0 && onProgress?.(Math.min(99, Math.round(((done + loaded) / total) * 100))),
    });
    done += draft.file.size + (draft.thumbnail?.size ?? 0);
    result.push({
      ...draft,
      file: undefined,
      thumbnail: undefined,
      url: uploaded.url,
      objectKey: uploaded.objectKey,
      thumbnailUrl: uploaded.thumbnailUrl,
      thumbnailObjectKey: uploaded.thumbnailObjectKey,
    });
  }
  onProgress?.(100);
  return result;
}

/** 업로드를 마친 첨부를 저장 API 요청 형태로 바꾼다. */
export function toMediaInputs(drafts: DraftMedia[]): MediaInput[] {
  return drafts.map((draft, index) => {
    if (!draft.url) throw new Error('업로드되지 않은 첨부가 있습니다.');
    return {
      mediaType: draft.mediaType,
      url: draft.url,
      objectKey: draft.objectKey,
      thumbnailUrl: draft.thumbnailUrl,
      thumbnailObjectKey: draft.thumbnailObjectKey,
      sortOrder: index,
    };
  });
}

export function formatBytes(bytes: number): string {
  const gb = 1024 ** 3;
  if (bytes >= gb) return `${+(bytes / gb).toFixed(1)}GB`;
  return `${Math.round(bytes / 1024 / 1024)}MB`;
}

// ---------------- 썸네일 ----------------

/** 이미지는 축소본, 동영상은 1초 지점(짧으면 중간) 화면을 400px JPG 로 만든다. 실패하면 null. */
export function makeThumbnail(file: File, mediaType: MediaType): Promise<Blob | null> {
  return mediaType === 'VIDEO' ? videoThumbnail(file) : imageThumbnail(file);
}

async function imageThumbnail(file: File): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return await drawJpeg(image, image.naturalWidth, image.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function videoThumbnail(file: File): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  try {
    video.src = url;
    await waitFor(video, 'loadeddata');
    video.currentTime = Number.isFinite(video.duration) ? Math.min(1, video.duration / 2) : 0;
    await waitFor(video, 'seeked');
    return await drawJpeg(video, video.videoWidth, video.videoHeight);
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

function waitFor(video: HTMLVideoElement, event: 'loadeddata' | 'seeked'): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => finish(new Error('timeout')), VIDEO_LOAD_TIMEOUT_MS);
    const onEvent = () => finish();
    const onError = () => finish(new Error('video error'));
    function finish(error?: Error) {
      window.clearTimeout(timer);
      video.removeEventListener(event, onEvent);
      video.removeEventListener('error', onError);
      if (error) reject(error);
      else resolve();
    }
    video.addEventListener(event, onEvent);
    video.addEventListener('error', onError);
  });
}

function drawJpeg(source: CanvasImageSource, width: number, height: number): Promise<Blob | null> {
  if (!width || !height) return Promise.resolve(null);
  const scale = Math.min(1, THUMBNAIL_MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext('2d');
  if (!context) return Promise.resolve(null);
  // JPG 는 투명도가 없어 그대로 두면 투명 PNG 배경이 검게 나온다.
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', THUMBNAIL_QUALITY));
}
